import { useState } from 'react'
import { postDataAPI } from '../../../lib/api'
import { uploadImage } from '../../../lib/uploadImage'
import { trackSignup, setGA4UserId, setGA4UserProperties } from '../../../lib/analytics'
import OnboardingShell from './OnboardingShell'
import type { SignupUser } from '../SignupPage'
import { useNavigate } from 'react-router-dom'
import TextField from '../../../components/ui/TextField'
import { OnboardingStep } from './steps'

export default function Step08TeamUsername({
  setStep,
  user,
  setUser,
}: {
  setStep: (n: number) => void
  user: SignupUser & { availableArtists?: any[] }
  setUser: React.Dispatch<React.SetStateAction<SignupUser>>
}) {
  const navigate = useNavigate()
  const [name, setName] = useState(user.name || '')
  const [avatar, setAvatar] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const role = user.professionRole === 'manager' || user.professionRole === 'support' || user.professionRole === 'promoter' ? user.professionRole : 'support'
  const roleLabel = role === 'manager' ? 'Member' : role === 'promoter' ? 'Promoter' : 'Support'

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setAvatar(file)
      const reader = new FileReader()
      reader.onloadend = () => setAvatarPreview(reader.result as string)
      reader.readAsDataURL(file)
    }
  }

  const createAccountAndNavigate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user.email || !name.trim()) return
    setError(null)
    setIsCreating(true)
    try {
      let avatarUrl = null
      if (avatar) {
        try {
          avatarUrl = await uploadImage(avatar)
        } catch (err) {
          console.error('Avatar upload failed:', err)
          // Continue without avatar
        }
      }

      const userToSubmit: any = {
        email: user.email,
        name: name.trim(),
        username: name.trim().toLowerCase().replace(/\s+/g, ''),
        professionRole: role,
        teamMemberPlan: user.teamMemberPlan || (role === 'manager' || role === 'promoter' ? 'basic' : 'support'),
        cover: null,
        avatar: avatarUrl,
      }

      const createRes = await postDataAPI('auth/add_user_info', userToSubmit)
      if (createRes?.status !== 200) throw new Error(createRes?.message || 'Failed to create account')

      const createdUser = createRes?.data?.user
      const token = createRes?.data?.token
      if (token) localStorage.setItem('dizeeToken', JSON.stringify(token))
      if (createdUser) localStorage.setItem('dizeeUser', JSON.stringify(createdUser))
      trackSignup('email')
      if (createdUser?._id) setGA4UserId(createdUser._id)
      setGA4UserProperties({ user_role: role, signup_method: 'email', has_team: 'true' })

      setUser((prev) => ({ ...prev, name: name.trim(), avatar: avatarUrl }))
      navigate('/team', { replace: true })
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to create account')
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <OnboardingShell
      title="What should we call you?"
      subtitle={`You're joining as a ${roleLabel}`}
      footer="This will be your display name when working with teams."
    >
      <form onSubmit={createAccountAndNavigate} className="space-y-5">
        {/* Avatar Upload */}
        <div className="flex flex-col items-center gap-3">
          <div className="relative">
            <div
              className="w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden"
              style={{ background: avatarPreview ? 'transparent' : 'linear-gradient(45deg, #6366f1, #8b5cf6)' }}
            >
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar preview" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-2xl font-semibold">
                  {name ? name.charAt(0).toUpperCase() : '?'}
                </span>
              )}
            </div>
            <label
              htmlFor="avatar-upload"
              className="absolute bottom-0 right-0 w-7 h-7 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-700 dark:hover:bg-gray-200 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </label>
            <input
              id="avatar-upload"
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Optional profile picture</p>
        </div>

        <TextField value={name} onChange={setName} placeholder="Enter your name" error={error} />

        <div className="flex gap-4 pt-2">
          <button type="button" onClick={() => setStep(OnboardingStep.TeamJoin)} className="ds-btn ds-btn-secondary flex-1">
            Go back
          </button>
          <button type="submit" disabled={!name.trim() || isCreating} className="ds-btn ds-btn-primary flex-1">
            {isCreating ? 'Creating account…' : 'Continue'}
          </button>
        </div>
      </form>
    </OnboardingShell>
  )
}
